import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';

import { runningTasks, taskProgressPct, useToolTasks } from '../../hooks';
import type { ToolTask, ToolTaskKind } from '../../types/api';
import styles from './TasksIndicator.module.css';

const KIND_LABEL: Readonly<Record<string, string>> = {
  'library.upload': 'Заливка вставок',
  'library.enrich': 'Авто-разметка',
  'voice.clone': 'Клонирование голоса',
  'voice.import': 'Импорт голосов',
  'subtitles.render': 'Субтитры',
};

function kindLabel(kind: ToolTaskKind): string {
  return KIND_LABEL[kind] ?? kind;
}

function progressLine(task: ToolTask): string | null {
  const progress = task.progress ?? {};
  const stage = typeof progress.stage === 'string' ? progress.stage : null;
  const total = Number(progress.total);
  const done = Number(progress.done ?? progress.processed ?? progress.completed);
  const counted =
    Number.isFinite(total) && total > 0 && Number.isFinite(done)
      ? `${done} из ${total}`
      : null;
  if (stage && counted) return `${stage} · ${counted}`;
  return stage ?? counted;
}

export function TasksIndicator() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data: tasks, isError } = useToolTasks();
  const running = runningTasks(tasks);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const count = running.length;
  const title = isError
    ? 'Список фоновых задач недоступен'
    : count > 0
      ? `Выполняется задач: ${count}`
      : 'Фоновых задач нет';

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={open ? `${styles.button} ${styles.buttonOpen}` : styles.button}
        onClick={() => setOpen((value) => !value)}
        title={title}
        aria-expanded={open}
      >
        <Clock size={14} strokeWidth={1.5} />
        <span className={styles.count}>{count}</span>
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Фоновые задачи">
          <div className={styles.panelHead}>
            <span className={styles.eyebrow}>фоновые задачи</span>
            <span className={styles.panelMeta}>
              {count} из {tasks?.length ?? 0} активны
            </span>
          </div>

          {isError && (
            <div className={styles.empty}>
              Не удалось получить список задач. Сами задачи при этом продолжают
              выполняться.
            </div>
          )}

          {!isError && running.length === 0 && (
            <div className={styles.empty}>Сейчас ничего не выполняется.</div>
          )}

          {running.map((task) => {
            const pct = taskProgressPct(task);
            const line = progressLine(task);
            return (
              <div key={task.id} className={styles.task}>
                <div className={styles.taskHead}>
                  <span className={styles.dot} aria-hidden="true" />
                  <span className={styles.taskName}>{kindLabel(task.kind)}</span>
                  <span className={styles.taskStatus}>
                    {task.status === 'pending' ? 'в очереди' : 'идёт'}
                  </span>
                </div>
                {pct !== null && (
                  <div className={styles.bar}>
                    <div className={styles.barFill} style={{ width: `${pct}%` }} />
                  </div>
                )}
                {line && <div className={styles.taskWhere}>{line}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
